/* eslint-disable */
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { listInvoices, deleteInvoice, downloadInvoicePdf, getCostControlYear } from "../lib/api";
import { formatEUR, formatPeriod, formatDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { FilePlus2, FileDown, Pencil, Trash2, Search, Eye, FolderOpen, ChevronDown, ChevronRight, Upload, CheckCircle2, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import BulkImportDialog from "../components/BulkImportDialog";
import { generateAdvanceDraft, generateFinalDraft, getDraftJob } from "../lib/api";

// Sort key: extracts a comparable string from the invoice number.
// Format expected: B/YY/NN or F25NNNN. We sort by (year, num) descending.
const sortKey = (inv) => {
  const m = (inv.number || "").match(/(\d{2})\/(\d+)/);  // B/26/03 -> [26,3]
  if (m) return { year: 2000 + Number(m[1]), num: Number(m[2]) };
  const f = (inv.number || "").match(/F(\d{2})(\d+)/);   // F250001 -> year 25, num 1
  if (f) return { year: 2000 + Number(f[1]), num: Number(f[2]) };
  return { year: inv.period_year || 0, num: 0 };
};

export default function Dashboard() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingFinal, setGeneratingFinal] = useState(false);
  const [collapsedYears, setCollapsedYears] = useState({});
  const [metricsYear, setMetricsYear] = useState(null); // null = auto (latest year)
  const [costBalance, setCostBalance] = useState(null); // {amount, monthLabel, direction}
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listInvoices();
      setInvoices(data);
    } catch {
      toast.error("Error cargando facturas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Load current cost-control balance for the latest year that has data
  useEffect(() => {
    const ES_MONTHS_SHORT = ["Ene","Feb","Mar","Abr","May","Jun",
                             "Jul","Ago","Sep","Oct","Nov","Dic"];
    const fetchBalance = async () => {
      const thisYear = new Date().getFullYear();
      for (const y of [thisYear, thisYear - 1]) {
        try {
          const d = await getCostControlYear(y);
          const m = d?.totals?.current_month || 0;
          const amt = d?.totals?.deuda_et_current;
          if (m > 0 && amt !== undefined) {
            setCostBalance({
              amount: amt,
              year: y,
              monthLabel: `${ES_MONTHS_SHORT[m - 1]} ${y}`,
            });
            return;
          }
        } catch {
          // ignore — keep trying next year
        }
      }
      setCostBalance({ amount: 0, year: thisYear, monthLabel: "sin movimientos" });
    };
    fetchBalance();
  }, []);

  const totals = useMemo(() => {
    const subtotalEur = (inv) => {
      const sub = inv.lines.reduce((a, l) => a + (l.amount || 0), 0);
      const total = sub * (1 + (inv.vat_rate || 0) / 100);
      if (inv.currency && inv.currency !== "EUR" && Number(inv.exchange_rate) > 0) {
        return total / Number(inv.exchange_rate);
      }
      return total;
    };
    const yearOf = (inv) => sortKey(inv).year;
    // Decide which year to display: explicit selection or latest available
    const allYears = [...new Set(invoices.map(yearOf))].sort((a, b) => b - a);
    const activeYear = metricsYear ?? allYears[0] ?? new Date().getFullYear();
    const yearInvoices = invoices.filter((i) => yearOf(i) === activeYear);
    const sum = (arr) => arr.reduce((s, inv) => s + subtotalEur(inv), 0);
    return {
      year: activeYear,
      availableYears: allYears,
      count: yearInvoices.length,
      total: sum(yearInvoices),
      advance: sum(yearInvoices.filter((i) => i.type === "advance")),
      final: sum(yearInvoices.filter((i) => i.type === "final")),
    };
  }, [invoices, metricsYear]);

  const filtered = useMemo(() => {
    if (!q.trim()) return invoices;
    const lq = q.toLowerCase();
    return invoices.filter(
      (i) =>
        i.number.toLowerCase().includes(lq) ||
        formatPeriod(i.period_month, i.period_year).toLowerCase().includes(lq) ||
        (i.type === "advance" ? "adelanto" : "final").includes(lq)
    );
  }, [invoices, q]);

  // Group by year (from sortKey.year), sorted descending by year, then by num descending inside
  const grouped = useMemo(() => {
    const byYear = {};
    filtered.forEach((inv) => {
      const k = sortKey(inv);
      const y = k.year;
      if (!byYear[y]) byYear[y] = [];
      byYear[y].push({ inv, key: k });
    });
    Object.values(byYear).forEach((arr) =>
      arr.sort((a, b) => b.key.num - a.key.num)
    );
    return Object.keys(byYear)
      .map(Number)
      .sort((a, b) => b - a)
      .map((y) => ({ year: y, items: byYear[y].map((x) => x.inv) }));
  }, [filtered]);

  const yearTotal = (items) =>
    items.reduce((s, inv) => {
      const sub = inv.lines.reduce((a, l) => a + (l.amount || 0), 0);
      if (inv.currency && inv.currency !== "EUR" && Number(inv.exchange_rate) > 0) {
        return s + sub / Number(inv.exchange_rate);
      }
      return s + sub;
    }, 0);

  const toggleYear = (y) => setCollapsedYears((s) => ({ ...s, [y]: !s[y] }));

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteInvoice(toDelete.id);
      toast.success(`Factura ${toDelete.number} eliminada`);
      setToDelete(null);
      load();
    } catch {
      toast.error("Error eliminando factura");
    }
  };

  const handleDownload = async (inv) => {
    try { await downloadInvoicePdf(inv.id, inv.number); }
    catch { toast.error("Error descargando PDF"); }
  };

  // Poll a draft-generation job until done or error. Max ~6 minutes.
  const pollDraftJob = async (jobId) => {
    for (let i = 0; i < 180; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const j = await getDraftJob(jobId);
        if (j.status === "done") return j.result;
        if (j.status === "error") throw new Error(j.error || "Error generando borrador");
      } catch (err) {
        if (err?.response?.status === 404) throw new Error("Job no encontrado");
        if (i > 5) throw err;
      }
    }
    throw new Error("Timeout esperando al CRM. Reintenta en unos minutos.");
  };

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Panel</div>
          <h1 className="font-cabinet text-4xl sm:text-5xl font-bold tracking-tighter text-slate-900 mt-1">
            Facturación
          </h1>
          <p className="text-slate-500 mt-2 max-w-lg">
            Facturas emitidas a Espíritu Travel LCC desde Viaja de Verdad S.L.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="rounded-md border-slate-200"
            onClick={async () => {
              if (!confirm("Esto extraerá los trips abiertos del Gestor en los 2 meses anteriores y creará un borrador de factura de adelanto. Puede tardar 1-2 minutos. ¿Continuar?")) return;
              setGenerating(true);
              const t = toast.loading("Extrayendo trips del Gestor… (puede tardar 1-2 min)");
              try {
                const job = await generateAdvanceDraft();
                const r = await pollDraftJob(job.job_id);
                if (r.trips_count === 0) {
                  toast.info(r.message || "Sin trips en el rango", { id: t });
                } else {
                  toast.success(`Borrador ${r.invoice_number} creado (${r.trips_count} trips · ${r.advance_eur.toFixed(2)} €)`, { id: t });
                  load();
                }
              } catch (err) {
                toast.error("Error: " + (err?.response?.data?.detail || err.message), { id: t });
              } finally {
                setGenerating(false);
              }
            }}
            disabled={generating}
            data-testid="generate-draft-btn"
          >
            {generating ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Sparkles size={15} className="mr-2" />}
            Generar borrador del mes
          </Button>
          <Button
            variant="outline"
            className="rounded-md border-slate-200"
            onClick={async () => {
              if (!confirm("Esto extraerá los trips CERRADOS sin factura final del Gestor (arrival ≥ 45 días antes de hoy) y creará un borrador de factura final. Puede tardar 1-2 minutos. ¿Continuar?")) return;
              setGeneratingFinal(true);
              const t = toast.loading("Extrayendo trips cerrados del Gestor… (puede tardar 1-2 min)");
              try {
                const job = await generateFinalDraft();
                const r = await pollDraftJob(job.job_id);
                if (r.trips_count === 0) {
                  toast.info(r.message || "Sin trips en el rango", { id: t });
                } else {
                  toast.success(`Borrador FINAL ${r.invoice_number} creado (${r.trips_count} trips · ${r.total_eur.toFixed(2)} €)`, { id: t });
                  load();
                }
              } catch (err) {
                toast.error("Error: " + (err?.response?.data?.detail || err.message), { id: t });
              } finally {
                setGeneratingFinal(false);
              }
            }}
            disabled={generatingFinal}
            data-testid="generate-final-draft-btn"
          >
            {generatingFinal ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Sparkles size={15} className="mr-2" />}
            Generar borrador final
          </Button>
          <Button
            variant="outline"
            className="rounded-md border-slate-200"
            onClick={() => setBulkOpen(true)}
            data-testid="bulk-import-btn"
          >
            <Upload size={15} className="mr-2" />
            Importar histórico
          </Button>
          <Button asChild variant="outline" className="rounded-md border-slate-200" data-testid="quick-new-final-btn">
            <Link to="/nueva/final">
              <FilePlus2 size={15} className="mr-2" />
              Factura Final
            </Link>
          </Button>
          <Button asChild className="rounded-md bg-slate-900 hover:bg-slate-800" data-testid="quick-new-advance-btn">
            <Link to="/nueva/adelanto">
              <FilePlus2 size={15} className="mr-2" />
              Factura Adelanto
            </Link>
          </Button>
        </div>
      </div>

      {/* Metrics */}
      <div className="flex items-center justify-between mt-2">
        <div className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">
          Resumen año
        </div>
        <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5" data-testid="metrics-year-tabs">
          {totals.availableYears.map((y) => (
            <button
              key={y}
              onClick={() => setMetricsYear(y)}
              data-testid={`metrics-year-${y}`}
              className={`px-3 py-1 text-xs font-semibold rounded-[5px] transition-colors ${
                totals.year === y
                  ? "bg-slate-900 text-white"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {y}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label={`Facturas ${totals.year}`} value={totals.count} testid="metric-count" />
        <MetricCard label={`Total ${totals.year}`} value={formatEUR(totals.total)} testid="metric-total" emphasis />
        <MetricCard label={`Adelantos ${totals.year}`} value={formatEUR(totals.advance)} testid="metric-advance" />
        <MetricCard label={`Finales ${totals.year}`} value={formatEUR(totals.final)} testid="metric-final" />
      </div>

      {/* Cost-control current balance (shared services VdV ↔ ET) */}
      {costBalance && (
        <Link
          to="/costes"
          data-testid="cost-balance-card"
          className={`flex items-center justify-between gap-4 rounded-lg border-2 px-5 py-4 transition-colors group ${
            costBalance.amount > 0
              ? "bg-rose-50 border-rose-200 hover:bg-rose-100"
              : costBalance.amount < 0
                ? "bg-emerald-50 border-emerald-200 hover:bg-emerald-100"
                : "bg-slate-50 border-slate-200 hover:bg-slate-100"
          }`}
        >
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-600">
              {costBalance.amount > 0
                ? "ET debe a VdV"
                : costBalance.amount < 0
                  ? "VdV debe a ET"
                  : "Balance VdV ↔ ET"}
            </div>
            <div className={`font-cabinet text-3xl font-bold mt-1 ${
              costBalance.amount > 0 ? "text-rose-700"
                : costBalance.amount < 0 ? "text-emerald-700" : "text-slate-500"
            }`} data-testid="cost-balance-amount">
              {formatEUR(Math.abs(costBalance.amount))}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Saldo actualizado a fin de <strong>{costBalance.monthLabel}</strong> · servicios compartidos al 50%
            </div>
          </div>
          <ChevronRight size={18} className="text-slate-400 group-hover:text-slate-600 transition-colors shrink-0" />
        </Link>
      )}

      {/* Search */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por número, periodo o tipo..."
            className="pl-9 bg-white"
          />
        </div>
      </div>

      {/* Year-grouped invoice tables (folders) */}
      {loading && (
        <div className="text-center py-10 text-slate-400 bg-white border border-slate-200 rounded-lg">Cargando...</div>
      )}
      {!loading && grouped.length === 0 && (
        <div className="text-center py-12 text-slate-400 bg-white border border-slate-200 rounded-lg">
          No hay facturas. Crea tu primera factura desde el botón superior o importa el histórico.
        </div>
      )}
      {grouped.map(({ year, items }) => {
        const collapsed = collapsedYears[year];
        return (
          <section key={year} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`year-folder-${year}`}>
            <button
              type="button"
              onClick={() => toggleYear(year)}
              className="w-full flex items-center justify-between px-5 py-3 bg-slate-50 hover:bg-slate-100 border-b border-slate-200 transition-colors"
              data-testid={`year-toggle-${year}`}
            >
              <div className="flex items-center gap-3">
                {collapsed ? <ChevronRight size={16} className="text-slate-500" /> : <ChevronDown size={16} className="text-slate-500" />}
                <FolderOpen size={16} className="text-slate-600" />
                <span className="font-cabinet text-lg font-bold tracking-tight text-slate-900">{year}</span>
                <Badge variant="outline" className="bg-white text-slate-600 border-slate-300">{items.length} facturas</Badge>
              </div>
              <div className="text-sm font-mono tabular-nums text-slate-900 font-semibold">
                {formatEUR(yearTotal(items))}
              </div>
            </button>
            {!collapsed && (
              <Table data-testid={`invoices-table-${year}`}>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-slate-200">
                    <TableHead className="font-semibold text-slate-900 text-xs uppercase tracking-wider">Nº</TableHead>
                    <TableHead className="font-semibold text-slate-900 text-xs uppercase tracking-wider">Tipo</TableHead>
                    <TableHead className="font-semibold text-slate-900 text-xs uppercase tracking-wider">Periodo</TableHead>
                    <TableHead className="font-semibold text-slate-900 text-xs uppercase tracking-wider">Emisión</TableHead>
                    <TableHead className="font-semibold text-slate-900 text-xs uppercase tracking-wider">Estado</TableHead>
                    <TableHead className="font-semibold text-slate-900 text-xs uppercase tracking-wider text-right">Total</TableHead>
                    <TableHead className="font-semibold text-slate-900 text-xs uppercase tracking-wider text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((inv) => {
                    const sub = inv.lines.reduce((a, l) => a + (l.amount || 0), 0);
                    const totalWithVat = sub * (1 + (inv.vat_rate || 0) / 100);
                    const isForeign = inv.currency && inv.currency !== "EUR" && Number(inv.exchange_rate) > 0;
                    const totalEur = isForeign ? totalWithVat / Number(inv.exchange_rate) : totalWithVat;
                    return (
                      <TableRow key={inv.id} className="border-slate-100 hover:bg-slate-50/60" data-testid={`invoice-row-${inv.number}`}>
                        <TableCell className="font-medium text-slate-900">
                          <div className="flex items-center gap-2">
                            <span>{inv.number}</span>
                            {(() => {
                              const ls = inv.last_gestion_sync;
                              if (!ls) return null;
                              const trips = inv.trips || [];
                              const okResults = (ls.results || []).filter((r) => r.status === "ok" || r.status === "manual_done");
                              const isFullySynced = ls.manual || (trips.length > 0 && okResults.length >= trips.filter((t) => {
                                if (inv.type === "advance") return Number(t.adelanto_eur || 0) > 0;
                                return Number((t.final_eur || 0) + (t.profit_share || 0)) > 0;
                              }).length);
                              if (isFullySynced) {
                                return (
                                  <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5" title={`Sincronizada${ls.manual ? " (manualmente)" : ""}`}>
                                    <CheckCircle2 size={10} /> Sync
                                  </span>
                                );
                              }
                              if (okResults.length > 0) {
                                return (
                                  <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5" title="Sincronización parcial">
                                    {okResults.length}/{trips.length}
                                  </span>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={
                              inv.type === "advance"
                                ? "bg-blue-50 text-blue-700 border-blue-100"
                                : "bg-emerald-50 text-emerald-700 border-emerald-100"
                            }
                          >
                            {inv.type === "advance" ? "Adelanto" : "Final"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-600">
                          {formatPeriod(inv.period_month, inv.period_year)}
                        </TableCell>
                        <TableCell className="text-slate-600">{formatDate(inv.issue_date)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={
                            inv.status === "issued"
                              ? "bg-slate-900 text-white border-slate-900"
                              : "bg-amber-50 text-amber-700 border-amber-100"
                          }>
                            {inv.status === "issued" ? "Emitida" : "Borrador"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono text-slate-900 tabular-nums">
                          <div>{formatEUR(totalEur)}</div>
                          {isForeign && (
                            <div className="text-[10px] text-slate-400">${sub.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} · TC {Number(inv.exchange_rate).toFixed(4)}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="inline-flex gap-1">
                            <Button size="icon" variant="ghost" title="Ver" data-testid={`view-${inv.number}`} onClick={() => nav(`/factura/${inv.id}`)}>
                              <Eye size={15} />
                            </Button>
                            <Button size="icon" variant="ghost" title="Editar" data-testid={`edit-${inv.number}`} onClick={() => nav(`/factura/${inv.id}/editar`)}>
                              <Pencil size={15} />
                            </Button>
                            <Button size="icon" variant="ghost" title="Descargar PDF" data-testid={`pdf-${inv.number}`} onClick={() => handleDownload(inv)}>
                              <FileDown size={15} />
                            </Button>
                            <Button size="icon" variant="ghost" title="Eliminar" data-testid={`delete-${inv.number}`} onClick={() => setToDelete(inv)}>
                              <Trash2 size={15} className="text-rose-600" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </section>
        );
      })}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar factura {toDelete?.number}?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-btn" onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BulkImportDialog open={bulkOpen} onOpenChange={setBulkOpen} onSuccess={load} />
    </div>
  );
}

function MetricCard({ label, value, suffix = "", testid, emphasis }) {
  return (
    <div
      data-testid={testid}
      className={`bg-white border border-slate-200 rounded-lg p-5 ${emphasis ? "ring-1 ring-slate-900/10" : ""}`}
    >
      <div className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">{label}</div>
      <div className="mt-3 flex items-baseline gap-1">
        <div className={`font-cabinet ${emphasis ? "text-2xl sm:text-3xl" : "text-2xl"} font-bold tracking-tight text-slate-900 break-words`}>
          {value}
        </div>
        {suffix && <span className="text-sm text-slate-500">{suffix}</span>}
      </div>
    </div>
  );
}
